import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { useEffect, useState } from "react";

import { actions$ } from "store/action-stream";
import { IEntityCreateFailure, RequestAction } from "store/entities/actions";
import { AGENT } from "store/entities/agents";
import { SUBSCRIPTION } from "store/entities/subscriptions";
import { IActionWithPayload } from "store/helper";

export enum PaymentFailedError {
  CardExpired = "card_expired",
  InsufficientFunds = "insufficient_funds",
  Generic = "generic",
}

const mapMessageToError = (message: string): PaymentFailedError => {
  switch (message) {
    case "Your credit card is expired, please update your card.":
      return PaymentFailedError.CardExpired;
    case "Your transaction was declined due to insufficient funds in your account. Please use a different card or contact your bank.":
      return PaymentFailedError.InsufficientFunds;
    default:
      return PaymentFailedError.Generic;
  }
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const onSubscriptionUpgradeFailed = (fn: (action: IActionWithPayload<string, { error: string }>) => void) =>
  pipe(
    actions$,
    filter(
      (action: IActionWithPayload<string, { error: string; errorObject?: IEntityCreateFailure["errorObject"] }>) =>
        (action.type === SUBSCRIPTION.UPDATE.FAILURE && action.payload.error !== "Plan limit exceeded") ||
        action.type === SUBSCRIPTION.CREATE.FAILURE ||
        (action.type === AGENT.CREATE_BATCH[RequestAction.FAILURE] &&
          action.payload?.errorObject?.error?.type === "paymentIssue")
    ),
    subscribe(fn)
  );

interface IUsePaymentFailure {
  failed: boolean;
  reset: () => void;
  error: PaymentFailedError;
}

export const usePaymentFailure = (): IUsePaymentFailure => {
  const [failed, setFailed] = useState(false);
  const [message, setMessage] = useState(PaymentFailedError.Generic);

  const reset = (): void => setFailed(false);

  useEffect(() => {
    const unsubscribe = onSubscriptionUpgradeFailed(action => {
      setFailed(true);
      setMessage(mapMessageToError(action.payload.error));
    });
    return unsubscribe;
  }, []);

  return { failed, reset, error: message };
};
